// AppointmentReceiptPDF.tsx
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logo from "../images/logoNew.png";

interface Appointment {
  appointmentId: string;
  formatedDate: string;
  formatedTime: string;
  doctorName: string;
  specializationName: string;
  doctorFees: string;
  specialty?: string;
}


interface patientInfo {
  name: string;
  gender: string;
  age: string;
 
}

interface Props {
  appointment: Appointment;
  onClose: () => void;
  patientInfo:patientInfo;
}


// ---------- Styles ----------
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    paddingTop: 20,
    paddingBottom: 40,
    paddingHorizontal: 40,
    lineHeight: 1.5,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderColor: "#ccc",
    paddingBottom: 6,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flex: 1,
    alignItems: "flex-end",
    justifyContent: "center",
  },
  hospitalName: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#009A85",
  },
  contactText: {
    fontSize: 8,
    marginTop: 2,
    color: "#444",
  },
  logo: {
    width: 80,
    height: 25,
    objectFit: "contain",
  },
  title: {
    backgroundColor: "#009A85",
    color: "white",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 11,
    marginTop: 10,
    paddingVertical: 3,
  },
  infoGrid: {
    marginTop: 8,
    marginBottom: 8,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  infoItem: {
    width: "50%",
    fontSize: 9,
    marginBottom: 2,
  },
  label: {
    fontWeight: "bold",
    color: "#009A85",
  },
  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#999",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderColor: "#999",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#ddd",
  },
  cell: {
    fontSize: 9,
    padding: 4,
    borderRightWidth: 1,
    borderColor: "#ddd",
  },
  footerNote: {
    marginTop: 20,
    backgroundColor: "#E6F7F4",
    fontSize: 9,
    padding: 6,
    color: "#333",
  },
  totals: {
    marginTop: 10,
    fontSize: 9,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  signatureRow: {
    marginTop: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8,
  },
  signatureBlock: {
    textAlign: "center",
  },
});

// ---------- Component ----------
const AppointmentReceiptPDF: React.FC<Props> = ({
  appointment,
  onClose,
  patientInfo
}) => {
    

  const total = appointment?.doctorFees
    ? (parseFloat(appointment.doctorFees)).toFixed(2)
    : "";

    const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    // year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <Text style={styles.hospitalName}>Rama Hospital</Text>
            <Text style={styles.contactText}>
              E-2 Sector 61, Noida, Uttar Pradesh - 201301
            </Text>
            <Text style={styles.contactText}>+91 9717898989, 9560687878</Text>
            <Text style={styles.contactText}>info@ramahospital.com</Text>
            <Text style={styles.contactText}>www.ramahospital.com</Text>
          </View>
          <View style={styles.headerRight}>
            <Image src={logo} style={styles.logo} />
          </View>
        </View>

        {/* Title */}
        <Text style={styles.title}>ONLINE APPOINTMENT INVOICE</Text>

        <View style={styles.infoGrid}>
          <Text style={styles.infoItem}>
            <Text style={styles.label}>Appointment ID: </Text>
            {appointment?.appointmentId || "N/A"}
          </Text>
          <Text style={styles.infoItem}>
            <Text style={styles.label}>Appointment Date & Time: </Text>
            {formatDate(appointment?.formatedDate)} - {appointment?.formatedTime}
          </Text>
          <Text style={styles.infoItem}>
            <Text style={styles.label}>Patient Name: </Text>
            {patientInfo?.name}
          </Text>
          <Text style={styles.infoItem}>
            <Text style={styles.label}>Sex/Age: </Text>
            {patientInfo?.gender}/ {patientInfo?.age}
          </Text>
          <Text style={styles.infoItem}>
            <Text style={styles.label}>Doctor Name: </Text>
            {appointment?.doctorName}
          </Text>
          <Text style={styles.infoItem}>
            <Text style={styles.label}>Specialization: </Text>
            {appointment?.specializationName}
          </Text>
          <Text style={styles.infoItem}>
            <Text style={styles.label}>Mode: </Text>
            Video Consultation
          </Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, { flex: 0.5 }]}>Sr.</Text>
            <Text style={[styles.cell, { flex: 2 }]}>Particulars</Text>
            <Text style={[styles.cell, { flex: 0.7 }]}>Qty</Text>
            <Text style={[styles.cell, { flex: 1 }]}>Fee (Rs.)</Text>
            <Text style={[styles.cell, { flex: 1 }]}>Disc. (Rs.)</Text>
            <Text style={[styles.cell, { flex: 1 }]}>Amount (Rs.)</Text>
          </View>
          {/* Row 1 */}
          <View style={styles.tableRow}>
            <Text style={[styles.cell, { flex: 0.5 }]}>1.</Text>
            <Text style={[styles.cell, { flex: 2 }]}>Doctor Consultation Fee</Text>
            <Text style={[styles.cell, { flex: 0.7 }]}>1</Text>
            <Text style={[styles.cell, { flex: 1 }]}>{appointment?.doctorFees || "0.00"}</Text>
            <Text style={[styles.cell, { flex: 1 }]}>0.00</Text>
            <Text style={[styles.cell, { flex: 1 }]}>{appointment?.doctorFees || "0.00"}</Text>
          </View>
          {/* Row 2 */}
          {/* <View style={styles.tableRow}>
            <Text style={[styles.cell, { flex: 0.5 }]}>2.</Text>
            <Text style={[styles.cell, { flex: 2 }]}>Platform / Service Charge</Text>
            <Text style={[styles.cell, { flex: 0.7 }]}>1</Text>
            <Text style={[styles.cell, { flex: 1 }]}>50.00</Text>
            <Text style={[styles.cell, { flex: 1 }]}>0.00</Text>
            <Text style={[styles.cell, { flex: 1 }]}>50.00</Text>
          </View> */}
        </View>

        {/* Totals */}
        <View style={styles.totals}>
          <View style={styles.totalRow}>
            <Text>Gross Amount:</Text>
            <Text>{total}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Discount:</Text>
            <Text>0.00</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Net Payable:</Text>
            <Text>{total}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Payment Status:</Text>
            <Text style={{ color: "green", fontWeight: "bold" }}>Paid</Text>
          </View>
        </View>

        {/* Footer Note */}
        <Text style={styles.footerNote}>
          Appointment Scheduled with {appointment?.doctorName} (
          {appointment?.specialty || appointment?.specializationName}). Please
          join the online session 10 minutes before scheduled time. A payment of
          Rs. {total} has been received successfully.
        </Text>

        {/* Signature */}
        <View style={styles.signatureRow}>
          <Text>
            Generated On: {new Date().toLocaleDateString("en-IN")}{" "}
            {new Date().toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </Text>
          <View style={styles.signatureBlock}>
            <Text>Signature</Text>
            <Text style={{ fontSize: 8 }}>
              System generated – no signature required
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default AppointmentReceiptPDF;
